import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const UserList = () => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [resetLoading, setResetLoading] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await api.getUsers();
        setUsers(data);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const Icon = ({ name, size = 16, className = '' }) => {
    const paths = {
      search: <><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>,
      plus: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
      edit: <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></>,
      key: <><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" /></>,
      trash: <><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>,
      arrow: <><polyline points="15 18 9 12 15 6" /></>,
      home: <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></>,
      close: <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>,
    };
    return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`pointer-events-none ${className}`}>{paths[name]}</svg>;
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const handleDelete = async (userId) => {
    try {
      await api.deleteUser(userId);
      setUsers(users.filter(u => u.id !== userId));
      setDeleteConfirm(null);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleResetPassword = async (user) => {
    if (!confirm(`Send password reset email to ${user.email}?`)) return;
    setResetLoading(user.id);
    try {
      const result = await api.request('/auth/admin-reset', {
        method: 'POST',
        body: JSON.stringify({ userId: user.id })
      });
      alert(result.message || 'Password reset email sent!');
    } catch (error) {
      alert('Failed to send reset email: ' + error.message);
    }
    setResetLoading(null);
  };

  return (
    <div className="min-h-screen bg-[#f8f9fb]" style={{ fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Header */}
      <header className="bg-white border-b border-[#e2e8f0] shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="w-10 h-10 rounded-lg bg-[#f1f5f9] flex items-center justify-center hover:bg-[#e2e8f0] transition-colors">
                <Icon name="arrow" size={20} className="text-[#64748b]" />
              </Link>
              <div>
                <h1 className="text-xl font-semibold text-[#0f172a]">User Management</h1>
                <p className="text-sm text-[#64748b]">{users.length} total users</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-2 h-10 px-4 bg-[#f1f5f9] text-[#475569] rounded-lg hover:bg-[#e2e8f0] transition-colors">
                <Icon name="home" size={16} />
                <span className="text-sm font-medium">Back to App</span>
              </Link>
              <Link to="/admin/users/new" className="flex items-center gap-2 h-10 px-4 bg-[#3b82f6] text-white rounded-lg hover:bg-[#2563eb] transition-colors">
                <Icon name="plus" size={16} />
                <span className="text-sm font-medium">Add User</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="bg-white rounded-xl border border-[#e2e8f0] p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Icon name="search" size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-11 pl-12 pr-4 border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/20 focus:border-[#3b82f6]"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="h-11 px-4 border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/20 focus:border-[#3b82f6] bg-white"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
            </select>
          </div>
        </div>

        {/* User Table */}
        <div className="bg-white rounded-xl border border-[#e2e8f0] overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-[#f8f9fb] border-b border-[#e2e8f0]">
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#64748b] uppercase tracking-wider">User</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#64748b] uppercase tracking-wider">Email</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#64748b] uppercase tracking-wider">Role</th>
                <th className="text-right px-6 py-4 text-xs font-semibold text-[#64748b] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f1f5f9]">
              {filteredUsers.map(user => (
                <tr key={user.id} className="hover:bg-[#f8f9fb] transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3b82f6] to-[#8b5cf6] flex items-center justify-center text-white font-medium">
                        {user.name[0]}
                      </div>
                      <div>
                        <p className="font-medium text-[#0f172a]">{user.name}</p>
                        {user.id === currentUser?.id && (
                          <span className="text-xs text-[#64748b]">(You)</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-[#475569]">{user.email}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${user.role === 'admin' ? 'bg-[#fef3c7] text-[#92400e]' : 'bg-[#dbeafe] text-[#1d4ed8]'}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/admin/users/${user.id}/edit`}
                        className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-[#f1f5f9] text-[#64748b] hover:text-[#3b82f6] transition-colors"
                        title="Edit user"
                      >
                        <Icon name="edit" size={16} />
                      </Link>
                      {user.id !== currentUser?.id && (
                        <button
                          onClick={() => handleResetPassword(user)}
                          disabled={resetLoading === user.id}
                          className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-[#fef3c7] text-[#64748b] hover:text-[#92400e] transition-colors disabled:opacity-50"
                          title="Reset password"
                        >
                          <Icon name="key" size={16} />
                        </button>
                      )}
                      {user.id !== currentUser?.id && (
                        <button
                          onClick={() => setDeleteConfirm(user)}
                          className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-[#fef2f2] text-[#64748b] hover:text-[#dc2626] transition-colors"
                        >
                          <Icon name="trash" size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center">
                    <p className="text-[#64748b]">No users found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#0f172a]">Delete User</h2>
              <button onClick={() => setDeleteConfirm(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f1f5f9]">
                <Icon name="close" size={20} className="text-[#64748b]" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-[#475569]">
                Are you sure you want to delete <span className="font-semibold">{deleteConfirm.name}</span>? This action cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-[#e2e8f0] flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="h-11 px-6 border border-[#e2e8f0] rounded-lg font-medium hover:bg-[#f8f9fb] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.id)}
                className="h-11 px-6 bg-[#dc2626] text-white rounded-lg font-medium hover:bg-[#b91c1c] transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
